import { useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CloudArrowUp, FileText, Check, Warning } from "@phosphor-icons/react";

const dataTypes = [
  { value: "form470", label: "Form 470 Data" },
  { value: "form471", label: "Form 471 Data" },
  { value: "form486", label: "Form 486 Data" },
  { value: "form472", label: "Form 472 (BEAR) Data" },
  { value: "form474", label: "Form 474 (SPI) Data" },
  { value: "c2_budget", label: "C2 Budget Data" },
  { value: "clients", label: "Client Data" },
];

export default function USACImport() {
  const [dataType, setDataType] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const handleFileChange = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setResult(null);

    const formData = new FormData();
    formData.append("file", f);

    try {
      const { data } = await api.post("/usac/preview", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (err) {
      toast.error("Failed to preview file");
      setPreview(null);
    }
  };

  const handleImport = async () => {
    if (!file || !dataType) {
      toast.error("Select a data type and file");
      return;
    }
    setImporting(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const { data } = await api.post(`/usac/import?data_type=${dataType}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Imported ${data.imported} records`);
    } catch (err) {
      toast.error("Import failed");
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="p-6" data-testid="usac-import-page">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280] mb-1">Data</p>
        <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D20]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>USAC Data Import</h1>
        <p className="text-sm text-[#6B7280] mt-1">Import CSV or Excel data from the USAC Open Data Portal</p>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Step 1: Select data type */}
        <div className="bg-white border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 bg-[#002FA7] flex items-center justify-center text-white text-xs font-bold">1</div>
            <p className="text-sm font-semibold text-[#1A1D20]">Select Data Type</p>
          </div>
          <Select value={dataType} onValueChange={setDataType}>
            <SelectTrigger data-testid="import-type-select" className="rounded-none"><SelectValue placeholder="Choose what type of data to import" /></SelectTrigger>
            <SelectContent className="rounded-none">
              {dataTypes.map((dt) => <SelectItem key={dt.value} value={dt.value}>{dt.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Step 2: Upload file */}
        <div className="bg-white border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 bg-[#002FA7] flex items-center justify-center text-white text-xs font-bold">2</div>
            <p className="text-sm font-semibold text-[#1A1D20]">Upload File</p>
          </div>
          <div
            className="border-2 border-dashed border-gray-300 p-8 text-center cursor-pointer hover:border-[#002FA7]/50 transition-colors"
            onClick={() => fileRef.current?.click()}
            data-testid="file-drop-zone"
          >
            <CloudArrowUp size={32} className="mx-auto text-[#6B7280] mb-3" />
            {file ? (
              <div className="flex items-center justify-center gap-2">
                <FileText size={16} className="text-[#002FA7]" />
                <span className="text-sm text-[#1A1D20] font-medium">{file.name}</span>
                <span className="text-xs text-[#6B7280]">({(file.size / 1024).toFixed(1)} KB)</span>
              </div>
            ) : (
              <>
                <p className="text-sm text-[#6B7280]">Click to select a CSV or Excel file</p>
                <p className="text-xs text-[#9CA3AF] mt-1">Supports .csv, .xlsx, .xls</p>
              </>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileChange}
            className="hidden"
            data-testid="file-input"
          />
        </div>

        {/* Preview */}
        {preview && (
          <div className="bg-white border border-gray-200 p-6" data-testid="import-preview">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-semibold text-[#1A1D20]">Preview ({preview.total_rows} rows, {preview.columns.length} columns)</p>
              <p className="text-xs text-[#6B7280]">Showing first {Math.min(10, preview.total_rows)} rows</p>
            </div>
            <div className="overflow-x-auto border border-gray-200">
              <Table>
                <TableHeader>
                  <TableRow className="data-table-header">
                    {preview.columns.map((col, i) => (
                      <TableHead key={i} className="py-2 px-3 text-xs uppercase tracking-wider text-[#6B7280] font-semibold whitespace-nowrap">{col}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.preview.map((row, ri) => (
                    <TableRow key={ri} className="border-b border-gray-100">
                      {preview.columns.map((col, ci) => (
                        <TableCell key={ci} className="py-2 px-3 text-xs whitespace-nowrap max-w-[200px] truncate">{String(row[col] ?? "")}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* Step 3: Import */}
        {preview && dataType && (
          <div className="bg-white border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 bg-[#002FA7] flex items-center justify-center text-white text-xs font-bold">3</div>
              <p className="text-sm font-semibold text-[#1A1D20]">Import Data</p>
            </div>
            <p className="text-sm text-[#6B7280] mb-4">
              Ready to import {preview.total_rows} rows into <span className="font-semibold text-[#1A1D20]">{dataTypes.find(d => d.value === dataType)?.label}</span>
            </p>
            <div className="flex gap-3">
              <button
                data-testid="import-btn"
                onClick={handleImport}
                disabled={importing}
                className="bg-[#002FA7] text-white hover:bg-[#002480] rounded-none font-medium px-6 py-2 text-sm transition-colors disabled:opacity-50"
              >
                {importing ? "Importing..." : "Import Now"}
              </button>
              <button
                data-testid="reset-import-btn"
                onClick={reset}
                className="bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-4 py-2 text-sm"
              >
                Reset
              </button>
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="bg-[#00B341]/5 border border-[#00B341]/20 p-6" data-testid="import-result">
            <div className="flex items-center gap-2 mb-2">
              <Check size={20} className="text-[#00B341]" weight="bold" />
              <p className="text-sm font-semibold text-[#00B341]">Import Complete</p>
            </div>
            <p className="text-sm text-[#1A1D20]">
              Successfully imported <span className="font-bold">{result.imported}</span> of {result.total_rows} records.
            </p>
            <p className="text-xs text-[#6B7280] mt-1">Columns: {result.columns.join(", ")}</p>
          </div>
        )}
      </div>
    </div>
  );
}
